import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminApi, errText, fmtDate } from "@/lib/api";
import { WHATSAPP_TEMPLATES, buildWhatsappLink } from "@/lib/whatsappTemplates";

const STATUSES = ["New", "Contacted", "Booked", "Visited", "Converted", "Lost"];
const SOURCES = ["Wheel", "Chat Game", "Booking", "Insurance", "Lead Form"];
const TEMPS = ["HOT", "WARM", "COLD"];
const ALL = "__all__";

const tempBadge = (t) => {
  if (t === "HOT") return <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 rounded-sm px-2 py-0.5">🔴 HOT</span>;
  if (t === "WARM") return <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 rounded-sm px-2 py-0.5">🟡 WARM</span>;
  return <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-[#F4F4F5] text-[#52525B] border border-[#E4E4E7] rounded-sm px-2 py-0.5">⚪ COLD</span>;
};

export function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({ status: "", source: "", temperature: "", search: "" });
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tplPerLead, setTplPerLead] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await adminApi.get("/admin/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const patch = async (lead, updates) => {
    try {
      const { data } = await adminApi.patch(`/admin/leads/${lead.id}`, updates);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
      toast.success("Updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await adminApi.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "autohq_leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const sendWhatsapp = (lead) => {
    const tplId = tplPerLead[lead.id] || "welcome";
    const tpl = WHATSAPP_TEMPLATES.find((t) => t.id === tplId);
    if (!tpl) return;
    window.open(buildWhatsappLink(lead.phone, tpl.build(lead)), "_blank", "noopener");
  };

  const filterSelect = (key, options, placeholder) => (
    <Select
      value={filters[key] || ALL}
      onValueChange={(v) => setFilters({ ...filters, [key]: v === ALL ? "" : v })}
    >
      <SelectTrigger data-testid={`leads-filter-${key}`} className="w-[140px] bg-white border-[#E4E4E7] h-9 text-xs text-[#0A0A0A]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="mt-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="leads-search-input"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          placeholder="Search phone, name or code…"
          className="w-56 bg-white border-[#E4E4E7] h-9 text-sm"
        />
        {filterSelect("status", STATUSES, "Status")}
        {filterSelect("source", SOURCES, "Source")}
        {filterSelect("temperature", TEMPS, "Temp")}
        <Button data-testid="leads-refresh-button" onClick={load} variant="outline" className="h-9 border-[#E4E4E7] bg-white text-[#52525B]">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <Button data-testid="leads-export-button" onClick={exportCsv} className="h-9 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-bold text-xs">
          <Download className="w-4 h-4 mr-1" /> CSV Export
        </Button>
        <span className="text-xs text-[#71717A] ml-auto">{leads.length} leads</span>
      </div>

      <div className="space-y-3" data-testid="leads-list">
        {leads.length === 0 && (
          <p className="text-[#71717A] text-sm py-10 text-center">No leads yet. They'll appear here as customers engage.</p>
        )}
        {leads.map((lead) => (
          <div key={lead.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4 hover:border-[#D4AF37] transition-colors" data-testid={`lead-row-${lead.phone}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-[150px]">
                <p className="font-bold text-sm text-[#0A0A0A]">{lead.name || "—"}</p>
                <p className="text-xs text-[#71717A]">{lead.phone} · {lead.car || "—"}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(lead.sources || []).map((s) => (
                  <span key={s} className="text-[10px] font-bold uppercase tracking-wide bg-[#F4F4F5] text-[#52525B] rounded-sm px-2 py-0.5">{s}</span>
                ))}
              </div>
              <span data-testid={`lead-temp-${lead.phone}`}>{tempBadge(lead.temperature)}</span>
              {lead.coupon_code && (
                <span className={`text-xs font-mono font-bold ${lead.redeemed ? "text-green-600 line-through" : "text-[#FF5A00]"}`}>
                  {lead.coupon_code}
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Select value={lead.status} onValueChange={(v) => patch(lead, { status: v })}>
                  <SelectTrigger data-testid={`lead-status-select-${lead.phone}`} className="w-[120px] bg-white border-[#E4E4E7] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  data-testid={`lead-expand-button-${lead.phone}`}
                  onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}
                  className="text-[#52525B] hover:text-[#0A0A0A] p-1"
                >
                  {expanded === lead.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* WhatsApp template row — always visible */}
            <div className="mt-3 pt-3 border-t border-[#F4F4F5] flex flex-wrap items-center gap-2">
              <Select
                value={tplPerLead[lead.id] || "welcome"}
                onValueChange={(v) => setTplPerLead({ ...tplPerLead, [lead.id]: v })}
              >
                <SelectTrigger data-testid={`lead-tpl-${lead.phone}`} className="w-[230px] h-8 text-xs bg-white border-[#E4E4E7]">
                  <SelectValue placeholder="Pick WhatsApp message" />
                </SelectTrigger>
                <SelectContent>
                  {WHATSAPP_TEMPLATES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid={`lead-wa-button-${lead.phone}`}
                onClick={() => sendWhatsapp(lead)}
                className="h-8 bg-[#25D366] hover:bg-[#1fb457] text-white font-bold text-xs"
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Send
              </Button>
              <a
                data-testid={`lead-phone-link-${lead.phone}`}
                href={`tel:+91${lead.phone}`}
                className="text-xs text-[#52525B] hover:text-[#0A0A0A] underline"
              >
                Call +91 {lead.phone}
              </a>
              {lead.coupon_code && !lead.redeemed && (
                <Button data-testid={`lead-redeem-button-${lead.phone}`} onClick={() => patch(lead, { redeemed: true })}
                  variant="outline" className="h-8 text-xs border-[#E4E4E7] text-[#52525B] ml-auto">
                  Mark Redeemed
                </Button>
              )}
            </div>

            {expanded === lead.id && (
              <div className="mt-4 pt-4 border-t border-[#E4E4E7] grid sm:grid-cols-2 gap-4 text-sm">
                <div className="space-y-1.5 text-xs text-[#71717A]">
                  <p>Prize: <span className="text-[#0A0A0A]">{lead.prize || "—"}</span></p>
                  <p>Coupon expiry: <span className="text-[#0A0A0A]">{fmtDate(lead.coupon_expiry)}</span></p>
                  <p>Service needed: <span className="text-[#0A0A0A]">{lead.service_needed || "—"}</span></p>
                  <p>Preferred time: <span className="text-[#0A0A0A]">{lead.preferred_time || "—"}</span> · day: <span className="text-[#0A0A0A]">{lead.preferred_day || "—"}</span></p>
                  <p>Service due: <span className="text-[#0A0A0A]">{lead.service_due || "—"}</span></p>
                  <p>Insurer: <span className="text-[#0A0A0A]">{lead.insurer || "—"}</span></p>
                  <p>Message: <span className="text-[#0A0A0A]">{lead.message || "—"}</span></p>
                  <p>Created: <span className="text-[#0A0A0A]">{fmtDate(lead.created_at)}</span></p>
                </div>
                <div className="space-y-3">
                  <Textarea
                    data-testid={`lead-notes-input-${lead.phone}`}
                    defaultValue={lead.notes || ""}
                    id={`notes-${lead.id}`}
                    placeholder="Notes…"
                    className="bg-[#FAFAFA] border-[#E4E4E7] text-sm text-[#0A0A0A] min-h-[70px]"
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`lead-followup-input-${lead.phone}`}
                      type="date"
                      defaultValue={lead.follow_up_date || ""}
                      id={`followup-${lead.id}`}
                      className="bg-[#FAFAFA] border-[#E4E4E7] h-9 text-xs w-40"
                    />
                    <Button
                      data-testid={`lead-save-button-${lead.phone}`}
                      onClick={() =>
                        patch(lead, {
                          notes: document.getElementById(`notes-${lead.id}`).value,
                          follow_up_date: document.getElementById(`followup-${lead.id}`).value,
                        })
                      }
                      className="h-9 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white text-xs font-bold"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
